import React from "react";

import { getDamPath } from "@utils/configuration/aem";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import ResultField from "@components/Calculator/ResultField/resultField.component";
import ResultTitle from "@components/Calculator/ResultTitle/resultTitle.component";
import { Col } from "@components/General/Grid/col.component";
import { Row } from "@components/General/Grid/row.component";

type Props = {
    brickWidth: number;
    brickLength: number;
    wallArea: number;
    numberOfBricksHalf: number;
    numberOfBricksFull: number;
    mortarVolumeHalf: number;
    mortarVolumeFull: number;
    jointCementVolume: number;
    mailParams: any;
};

const Result = ({
    brickWidth,
    brickLength,
    wallArea,
    numberOfBricksHalf,
    numberOfBricksFull,
    mortarVolumeHalf,
    mortarVolumeFull,
    jointCementVolume,
    mailParams,
}: Props) => {
    const { t } = useGranite();

    const wallWidthInMetersHalf = Number(brickWidth * 0.01);
    const wallWidthInMetersFull = Number(brickLength * 0.01);

    // half stonewall
    const cementInKgHalf = Number(mortarVolumeHalf * 300 * 1.1);
    const sandInKgHalf = Number(mortarVolumeHalf * 1500 * 1.1);
    const waterHalf = Number(mortarVolumeHalf * 150);
    const cementInBagsOf20KgHalf = Number(Math.ceil(cementInKgHalf / 20));
    const cementInBagsOf25KgHalf = Number(Math.ceil(cementInKgHalf / 25));
    const sandInBagsOf20KgHalf = Number(Math.ceil(sandInKgHalf / 20));
    const sandInBagsOf25KgHalf = Number(Math.ceil(sandInKgHalf / 25));

    // full stonewall
    const cementInKgFull = Number(mortarVolumeFull * 300 * 1.1);
    const sandInKgFull = Number(mortarVolumeFull * 1500 * 1.1);
    const waterFull = Number(mortarVolumeFull * 150);
    const cementInBagsOf20KgFull = Number(Math.ceil(cementInKgFull / 20));
    const cementInBagsOf25KgFull = Number(Math.ceil(cementInKgFull / 25));
    const sandInBagsOf20KgFull = Number(Math.ceil(sandInKgFull / 20));
    const sandInBagsOf25KgFull = Number(Math.ceil(sandInKgFull / 25));

    // jointcement single side
    const jointCementInKg = Number(jointCementVolume * 350 * 1.1);
    const jointSandInKg = Number(jointCementVolume * 1500 * 1.1);
    const jointWater = Number(jointCementVolume * 175);
    const jointCementInBagsOf20Kg = Number(Math.ceil(jointCementInKg / 20));
    const jointCementInBagsOf25Kg = Number(Math.ceil(jointCementInKg / 25));
    const jointSandInBagsOf20Kg = Number(Math.ceil(jointSandInKg / 20));
    const jointSandInBagsOf25Kg = Number(Math.ceil(jointSandInKg / 25));

    // jointcement double side
    const jointCementInKgDoubleSide = Number(2 * jointCementVolume * 350 * 1.1);
    const jointSandInKgDoubleSide = Number(2 * jointCementVolume * 1500 * 1.1);
    const jointWaterDoubleSide = Number(2 * jointCementVolume * 175);
    const jointCementInBagsOf20KgDoubleSide = Number(
        Math.ceil((2 * jointCementInKg) / 20),
    );
    const jointCementInBagsOf25KgDoubleSide = Number(
        Math.ceil((2 * jointCementInKg) / 25),
    );
    const jointSandInBagsOf20KgDoubleSide = Number(
        Math.ceil((2 * jointSandInKg) / 20),
    );
    const jointSandInBagsOf25KgDoubleSide = Number(
        Math.ceil((2 * jointSandInKg) / 25),
    );

    return (
        <div className={"mt-4"}>
            <ResultTitle mailParams={mailParams} />
            <Row id="resultTop">
                <ResultField
                    copy={t("calculator.brickCalculator.wallArea")}
                    value={Number(wallArea).toFixed(2)}
                    unit={"m"}
                    sup={2}
                    md={6}
                />
            </Row>
            <Row id="resultBottom">
                <Col id="half" sm="6">
                    <Row>
                        <Col sm={12} md={6} lg={6}>
                            <h3 className="d-sm-block">
                                {t("calculator.brickCalculator.halfBrick")}
                            </h3>
                            <p>
                                {t(
                                    "calculator.brickCalculator.halfBrickDetails",
                                )}
                            </p>
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <img
                                className={"my-2 w-100"}
                                src={`${getDamPath()}/hulp-en-advies/calculators/muur-halve-steen.jpg`}
                                alt={"muur-halve-steen"}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.brickCalculator.wallWidth")}
                            value={wallWidthInMetersHalf.toFixed(2)}
                            unit={"m"}
                            md={6}
                        />
                        <ResultField
                            copy={t(
                                "calculator.brickCalculator.numberOfBricks",
                            )}
                            value={Number(numberOfBricksHalf).toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <h4>{t("calculator.brickCalculator.grout")}</h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={Number(mortarVolumeHalf).toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={cementInKgHalf.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={cementInBagsOf20KgHalf}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={cementInBagsOf25KgHalf}
                            md={6}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.rhineSand")}
                            value={sandInKgHalf.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={sandInBagsOf20KgHalf.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={sandInBagsOf25KgHalf.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={waterHalf.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <h4>
                                {t("calculator.brickCalculator.jointCement")}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={Number(jointCementVolume).toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={jointCementInKg.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointCementInBagsOf20Kg.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointCementInBagsOf25Kg.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.whiteSand")}
                            value={jointSandInKg.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointSandInBagsOf20Kg.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointSandInBagsOf25Kg.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={jointWater.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                </Col>
                <Col id="fullBrick" sm="6">
                    <Row>
                        <Col sm={12} md={6} lg={6}>
                            <h3 className="d-sm-block">
                                {t("calculator.brickCalculator.fullBrick")}
                            </h3>
                            <p className="d-sm-block">
                                {t(
                                    "calculator.brickCalculator.fullBrickDetails",
                                )}
                            </p>
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <img
                                className={"my-2 w-100"}
                                src={`${getDamPath()}/hulp-en-advies/calculators/muur-volle-steen.jpg`}
                                alt={"muur-volle-steen"}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.brickCalculator.wallWidth")}
                            value={wallWidthInMetersFull.toFixed(2)}
                            unit={"m"}
                            md={6}
                        />
                        <ResultField
                            copy={t(
                                "calculator.brickCalculator.numberOfBricks",
                            )}
                            value={Number(numberOfBricksFull).toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <h4>{t("calculator.brickCalculator.grout")}</h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={Number(mortarVolumeFull).toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={cementInKgFull.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={cementInBagsOf20KgFull}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={cementInBagsOf25KgFull}
                            md={6}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.rhineSand")}
                            value={sandInKgFull.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={sandInBagsOf20KgFull}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={sandInBagsOf25KgFull}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={waterFull.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <h4>
                                {t("calculator.brickCalculator.jointCement")}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={Number(jointCementVolume).toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={jointCementInKgDoubleSide.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointCementInBagsOf20KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointCementInBagsOf25KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row className={"calculator-group"}>
                        <ResultField
                            copy={t("calculator.whiteSand")}
                            value={jointSandInKgDoubleSide.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointSandInBagsOf20KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointSandInBagsOf25KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={jointWaterDoubleSide.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default Result;
